import Chip from '@mui/material/Chip';
import { AccountType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { SyntheticEvent, useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import { Account } from '~hooks/useAccount/models';
import useAccount from '~hooks/useAccount/useAccount';
import { CompanyBasic } from '~hooks/useCompany/models';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';

interface OrderCustomerInputV2Props {
  companyId?: string;
  prefilledValue?: string;
}

export default function OrderCustomerInputV2({
  companyId,
  prefilledValue,
}: OrderCustomerInputV2Props) {
  const form = useFormContext();

  const { toasterStore } = useStores();
  const { createAccount, getAccountsByCompanyIdTypeahead } = useAccount();

  const [tmpAccountName, setTmpAccountName] = useState<string>('');
  const onInputAccountName = (event: SyntheticEvent, value: string) => {
    setTmpAccountName(value);
  };

  const watchedSelectedCompany = form.watch('company') as CompanyBasic | null;

  const fetchAccountsByCompanyId = useCallback(
    async (additionalProps = {}) => {
      if (!companyId) {
        return [];
      }

      return await getAccountsByCompanyIdTypeahead({
        companyId,
        accountTypes: [AccountType.CUSTOMER],
        ...additionalProps,
      });
    },
    [companyId],
  );

  const createNewAccount = async () => {
    if (!watchedSelectedCompany?.id) {
      toasterStore.push(
        alert(t('form_validation_errors.no_user_company'), AlertTypes.error),
      );
      return;
    }

    try {
      const account = await createAccount({
        account: {
          name: tmpAccountName,
          accountTypes: [AccountType.CUSTOMER],
          company: watchedSelectedCompany as CompanyBasic,
          connectedCompany: watchedSelectedCompany as CompanyBasic,
        } as unknown as Account,
      });

      toasterStore.push(
        alert(t('account.account_created', { name: account.name }), AlertTypes.success),
      );
      form.setValue('account', account);
    } catch (error) {
      void error;
    }
  };

  return (
    <AutocompleteAsyncFormField
      asyncCallback={fetchAccountsByCompanyId}
      control={form.control}
      disabled={!companyId}
      errors={form.formState.errors}
      getLabel={(item) => item.name || ''}
      getValue={(item) => item.id}
      isRequired
      label={`${t('dispatch.order.customer_account')}`}
      name="account"
      clearable={true}
      prefilledValue={prefilledValue}
      onInput={onInputAccountName}
      noOptionsText={
        <Chip
          size={'small'}
          variant={'filled'}
          color={'primary'}
          onClick={() => void createNewAccount()}
          sx={{ cursor: 'pointer' }}
          label={t('account.create_account')}
        />
      }
      dataUi={`page-sales-orders__order-customer-input-v2__account-input`}
    />
  );
}
